import { Component, TemplateRef } from '@angular/core';
import { ToolBaseComponent } from '../tool-base';
import { ApiService } from 'src/app/shared/services/api.service';
import { EngineeringToolsApiService } from 'src/app/shared/services/engineering-tools-api';
import { UnitConverterService } from 'src/app/shared/services/unit-converter.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MeasurementContext, MotorBearingTypeDescriptions, MotorConduitBoxDescriptions, MotorEfficiencyDescriptions, MotorEnclosureDescriptions, MotorInsulationDescriptions, MotorManufacturerDescriptions } from 'src/app/shared/data/engineering-enums';

interface MotorDetailField {
  key: string;
  label: string;
  context?: MeasurementContext;
  descriptions?: ReadonlyMap<number, string>;
  boolean?: boolean;
  unit?: string;
}

@Component({
  selector: 'app-motor-selector',
  templateUrl: './motor-selector.html',
  styleUrl: './motor-selector.scss',
  standalone: false
})
export class MotorSelectorComponent extends ToolBaseComponent {
  manufacturerDescriptions = MotorManufacturerDescriptions;
  enclosureDescriptions = MotorEnclosureDescriptions;
  efficiencyDescriptions = MotorEfficiencyDescriptions;
  expandedMotor: any = null;
  filterViewModel: any = {};
  private filterDialogRef: MatDialogRef<unknown> | null = null;

  motorDetailFields: MotorDetailField[] = [
    { key: 'Power', label: 'Power', context: MeasurementContext.Power },
    { key: 'Voltage', label: 'Voltage', unit: 'V' },
    { key: 'Phase', label: 'Phase' },
    { key: 'Frequency', label: 'Frequency', unit: 'Hz' },
    { key: 'Manufacturer', label: 'Manufacturer', descriptions: MotorManufacturerDescriptions },
    { key: 'Efficiency', label: 'Efficiency Class', descriptions: MotorEfficiencyDescriptions },
    { key: 'InverterDuty', label: 'Inverter Duty', boolean: true },
    { key: 'Frame', label: 'Frame' },
    { key: 'CFace', label: 'C Face', boolean: true },
    { key: 'Enclosure', label: 'Enclosure', descriptions: MotorEnclosureDescriptions },
    { key: 'Weight', label: 'Weight', context: MeasurementContext.Weight },
    { key: 'Poles', label: 'Poles' },
    { key: 'Speed', label: 'Speed', context: MeasurementContext.Speed },
    { key: 'Insulation', label: 'Insulation', descriptions: MotorInsulationDescriptions },
    { key: 'BearingType', label: 'Bearing Type', descriptions: MotorBearingTypeDescriptions },
    { key: 'LockedRotorCode', label: 'Locked Rotor Code' },
    { key: 'FullLoadAmps', label: 'Full Load Amps', unit: 'A' },
    { key: 'LockedRotorAmps', label: 'Locked Rotor Amps', unit: 'A' },
    { key: 'EfficiencyValue', label: 'Efficiency', unit: '%' },
    { key: 'PowerFactor', label: 'Power Factor', unit: '%' },
    { key: 'FullLoadTorque', label: 'Full Load Torque', context: MeasurementContext.Torque },
    { key: 'LockedRotorTorque', label: 'Locked Rotor Torque', context: MeasurementContext.Torque },
    { key: 'PullUpTorque', label: 'Pull Up Torque', context: MeasurementContext.Torque },
    { key: 'Dimensions', label: 'Dimensions' },
    { key: 'ConduitBox', label: 'Conduit Box', descriptions: MotorConduitBoxDescriptions },
    { key: 'DFlange', label: 'D Flange', boolean: true },
    { key: 'SpaceHeaters', label: 'Space Heaters', boolean: true },
    { key: 'Model', label: 'Model' },
    { key: 'Mods', label: 'Mods' },
    { key: 'ShaftStiffness', label: 'Shaft Stiffness' },
    { key: 'MomentOfInertia', label: 'Moment of Inertia' },
    { key: 'Efficiency75', label: 'Efficiency at 75% Load', unit: '%' },
    { key: 'PowerFactor75', label: 'Power Factor at 75% Load', unit: '%' },
    { key: 'Efficiency50', label: 'Efficiency at 50% Load', unit: '%' },
    { key: 'PowerFactor50', label: 'Power Factor at 50% Load', unit: '%' },
  ];

  toggleMotorDetails(motor: any) {
    this.expandedMotor = this.expandedMotor === motor ? null : motor;
  }

  motorDetailValue(motor: any, field: MotorDetailField): string {
    const value = motor[field.key];
    if (value == null) return '';
    if (field.context !== undefined) {
      const formatted = this.formatUnits(value, field.context, 2);
      return formatted ? formatted + ' ' + this.getUnitText(field.context) : '';
    }
    if (field.descriptions) return field.descriptions.get(value) ?? String(value);
    if (field.boolean) return value ? 'Yes' : 'No';
    return String(value) + (field.unit ? ' ' + field.unit : '');
  }

  filterInputs = [
    { key: 'Search', label: 'Search', type: 'search', removable: true },
    { key: 'PowerConsumption', label: 'Power Consumption', type: 'power', removable: true },
    { key: 'Manufacturer', label: 'Manufacturer', type: 'dropdown', removable: true },
    { key: 'Voltage', label: 'Voltage', type: 'dropdown', removable: true },
    { key: 'Enclosure', label: 'Enclosure', type: 'dropdown', removable: true },
    { key: 'InverterDuty', label: 'Inverter Duty', type: 'text', removable: true },
    { key: 'Poles', label: 'Poles', type: 'dropdown', removable: true },
    { key: 'NominalSpeed', label: 'Nominal Speed', type: 'dropdown', removable: true },
  ];

  get activeFilters() {
    return this.filterInputs.filter(filter => {
      const value = this.viewModel[filter.key]?.Value;
      const selection = filter.type === 'power' ? value?.Value : value;
      if (filter.type === 'power' && Number(selection) === 0) return false;
      return selection != null && String(selection).trim() !== '' && selection !== -1 && selection !== '-1';
    }).map(filter => {
      const input = this.viewModel[filter.key];
      let text = String(input.Value);
      if (filter.type === 'power') {
        text = this.formatMeasurementToDisplay(input.Value, this.MeasurementContext.Power, 0)
          + ' ' + this.getUnitText(this.MeasurementContext.Power);
      } else if (filter.type === 'dropdown') {
        text = input.AvailableList?.Value?.find((option: { Key: any; Value: string }) => option.Key === input.Value)?.Value ?? text;
      }
      return { ...filter, text };
    });
  }

  trackFilter(index: number, filter: { key: string }): string {
    return filter.key;
  }

  openFilters(template: TemplateRef<unknown>) {
    this.filterViewModel = structuredClone(this.viewModel);
    this.filterDialogRef = this.dialog.open(template, {
      width: '770px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: true,
    });
  }

  cancelFilterChanges() {
    this.filterDialogRef?.close();
  }

  applyFilterChanges() {
    this.viewModel = this.filterViewModel;
    this.filterDialogRef?.close();
    this.onChange(null);
  }

  clearFilter(key: string, update: boolean = true) {
    const filter = this.filterInputs.find(input => input.key === key);
    if (filter?.type === 'power') {
      this.viewModel[key].Value = { ...this.viewModel[key].Value, Value: 0 };
    } else {
      this.viewModel[key].Value = filter?.type === 'dropdown' ? -1 : '';
    }
    if (update) this.onChange(null);
  }

  resetAllFilters() {
    this.filterInputs.forEach(filter => this.clearFilter(filter.key, false));
    this.viewModel.MotorMultiplier.Value = 1;
    this.onChange(null);
  }

  constructor(
    protected override hrtApi: ApiService,
    protected override api: EngineeringToolsApiService,
    protected override unitConverter: UnitConverterService,
    public dialog: MatDialog,
    private readonly route: ActivatedRoute,
  ) {
    super(hrtApi, api, unitConverter);
    this.componentType = "MotorSelector";
  }

  ngOnInit() {
    this.initializeFromRouteIdOrCreate(this.route);
  }

  private searchDelayed: ReturnType<typeof setTimeout> | undefined;

  searchTextChanged() {
    clearTimeout(this.searchDelayed);
    this.searchDelayed = setTimeout(() => this.onChange(null), 500);
  }

  ngOnDestroy() {
    clearTimeout(this.searchDelayed);
  }

  onChange(event: any) {
    clearTimeout(this.searchDelayed);
    this.updateComponent();
  }
}
